import { useEffect } from "react";
import {
	Box,
	Button,
	Flex,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Stack
} from "@chakra-ui/react";
import { RiMapPinLine } from "react-icons/ri";
import { FaGoogle } from "react-icons/fa";
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { v4 as uuid } from 'uuid';
import SearchGoogle from "../SearchGoogle";
import { geocodeByPlaceId, getLatLng }
	from "react-google-places-autocomplete";

import api from "../../services/api";
import { useAuth } from "../../hooks/auth";
import { Input } from "../Inputs/Input";

type EndFormData = {
	cep: string;
	uf: string;
	cid: string;
	cidcod: string;
	bai: string;
	endereco: string;
	num: string;
	comp?: string;
	ponto_ref?: string;
}
type EndData = {
  id: string;
  cep: string;
  uf: string;
  cid: string;
  cidcod: string;
  bai: string;
  endereco: string;
  num: string;
  comp?: string;
  ponto_ref?: string;
  emp_cod: string;
}

interface NewEndClienteModalProps {
	isOpen: boolean;
	onRequestClose: () => void;
	setEnderecos: (enderecos: EndData[]) => void;
	enderecos: EndData[];
	endModal: EndData;
	setEndModal: (end: EndData) => void;
}

const enderecoFormSchema: yup.SchemaOf<EndFormData> = yup.object().shape({
	cep: yup.string().required('Cep obrigatório.'),
	uf: yup.string().required('Estado obrigatório.'),
	cid: yup.string().required('Cidade obrigatório.'),
	cidcod: yup.string().required(),
	bai: yup.string().required('Bairro obrigatório.'),
	endereco: yup.string().required('Endereço obrigatório.'),
	num: yup.string().required('Número obrigatório.'),
	comp: yup.string(),
	ponto_ref: yup.string(),
})

const NewEndClienteModal: React.FC<NewEndClienteModalProps> = ({
	isOpen,
	onRequestClose,
	setEnderecos,
	enderecos,
	endModal,
	setEndModal,
}) => {
	const { company, token } = useAuth();

	const { register, handleSubmit, formState, setValue, reset, setFocus,  } = useForm<EndFormData>({
		resolver: yupResolver(enderecoFormSchema)
	});

	useEffect(() => {
		reset(endModal);
	}, []);

	function handleMasktCep(event: string) {
		let res = event;
		res = res.replace(/[A-Za-z]/g, '');
		setValue('cep', res);
	};

	async function handleCep(value: string, isGoogleSearch: boolean) {
		let cep = value;
		// Verifica se campo cep possui valor informado
		if (cep !== '' || cep !== undefined || cep !== null) {
			cep = cep.replace(/\D/g, '');
			// Expressão regular para validar o CEP.
			const validacep = /^[0-9]{8}$/;
			// Valida o formato do CEP.
			if (validacep.test(cep)) {
				delete api.defaults.headers.common['Authorization'];
				delete api.defaults.headers.common['company'];

				await api
					.get(`https://viacep.com.br/ws/${cep}/json`)
					.then(response => {
						if (!isGoogleSearch) {
							setValue('cid', response.data.localidade);
							setValue('bai', response.data.bairro);
							setValue(
								'endereco',
								response.data.logradouro,
							);
							setValue('uf', response.data.uf);
							setValue('comp', response.data.complemento);

							setFocus('num');
						}

						setValue('cidcod', response.data.ibge);
					})
					.catch(err => {
						/* addToast({
							type: 'error',
							title: 'Erro no CEP',
							description: `ERROR - ${err}`,
						}); */
					});

				api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
				api.defaults.headers.common['company'] = company.id;
			} else {
				/* addToast({
					type: 'error',
					title: 'Erro no CEP',
					description: 'CEP digitado é inválido.',
				}); */
			}
		}
	};

	async function onLocationSelected(data: any) {
		await geocodeByPlaceId(data.value.place_id)
			.then(results => {
				const end = results[0].address_components;
				handleCep(end.length === 7 ? end[6].short_name : end.length === 6 ? end[5].short_name : '', true);
				setValue('cep', end.length === 7 ? end[6].short_name : end.length === 6 ? end[5].short_name : '');
				setValue('uf', end.length === 7 ? end[4].short_name : end.length === 6 ? end[3].short_name : '');
				setValue('cid', end.length === 7 ? end[3].short_name : end.length === 6 ? end[2].short_name : '');
				setValue('bai', end.length === 7 ? end[2].short_name : end.length === 6 ? end[1].short_name : '');
				setValue('endereco', end.length === 7 ? end[1].short_name : end.length === 6 ? end[0].short_name : '');
				setValue('num', end.length === 7 ? end[0].short_name : '');
				setFocus(end.length === 7 ? 'comp' : 'num');

				return results;
			})
			.then(results => getLatLng(results[0]))
			// .then(location => console.log(location))
			// .catch(error => console.log(error))
	};

	const handleSubmitEndereco: SubmitHandler<EndFormData> = async (data) => {
		if (endModal.id) {
			const endIndex = enderecos.findIndex(end => end.id === endModal.id);
      enderecos[endIndex] = {
        ...data,
        id: enderecos[endIndex].id,
        emp_cod: enderecos[endIndex].emp_cod,
        comp: data.comp ? data.comp : '',
        ponto_ref: data.ponto_ref ? data.ponto_ref : ''
      };
			setEnderecos(enderecos);
		} else {
			const endereco = { ...data, id: uuid(), emp_cod: company.id };
			setEnderecos([...enderecos, endereco]);
		}

		setEndModal({} as EndData);
		reset();
		onRequestClose();
	}

	return (
		<Modal
			isCentered={true}
			closeOnOverlayClick={false}
			blockScrollOnMount={false}
			size="lg"
			isOpen={isOpen}
			onClose={onRequestClose}
		>

			<ModalOverlay />
			<ModalContent mt="60" as="form" onSubmit={handleSubmit(handleSubmitEndereco)}>
				<ModalHeader>Novo Endereço</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<Stack>
						<Box mb="4">
							<SearchGoogle
								onLocationSelected={onLocationSelected}
								label="Buscar Endereço"
								icon={FaGoogle}
							/>
						</Box>

						<Input
							label="CEP"
							iconLabel={RiMapPinLine}
              bgColor="gray.50"
							{...register("cep")}
							error={formState.errors.cep}
							onChange={(e: any) => handleMasktCep(e.target.value)}
              onBlur={(e: any) => handleCep(e.target.value, false)}
						/>

						{/* CIDADE, ESTADO */}
						<Flex>
							<Input
								label="Cidade"
								iconLabel={RiMapPinLine}
                bgColor="gray.50"
								{...register("cid")}
								error={formState.errors.cid}
							/>

							<Box ml="4">
								<Input
									label="Estado"
                  iconLabel={RiMapPinLine}
									bgColor="gray.50"
									{...register("uf")}
									error={formState.errors.uf}
								/>
							</Box>
						</Flex>

						{/* BAIRRO */}
						<Input
							label="Bairro"
							iconLabel={RiMapPinLine}
              bgColor="gray.50"
							{...register("bai")}
							error={formState.errors.bai}
						/>

						{/* ENDEREÇO */}
						<Input
							label="Endereço"
              iconLabel={RiMapPinLine}
              bgColor="gray.50"
							{...register("endereco")}
							error={formState.errors.endereco}
						/>

						{/* NÚMERO, COMPLEMENTO */}
						<Flex>
							<Box mr="4">
								<Input
									label="Número"
									bgColor="gray.50"
                  iconLabel={RiMapPinLine}
									{...register("num")}
									error={formState.errors.num}
								/>
							</Box>

							<Input
								label="Complemento"
                iconLabel={RiMapPinLine}
                bgColor="gray.50"
								{...register("comp")}
								error={formState.errors.comp}
							/>
						</Flex>

						{/* PONTO DE REFERENCIA */}
						<Input
							label="Ponto de referência"
              iconLabel={RiMapPinLine}
              bgColor="gray.50"
							{...register("ponto_ref")}
							error={formState.errors.ponto_ref}
						/>

						{/* CÓDIGO DA CIDADE */}
						<Input
							label="Cidade Cod"
							bgColor="gray.50"
							{...register("cidcod")}
							error={formState.errors.cidcod}
							display="none"
							visibility="hidden"
						/>
					</Stack>
				</ModalBody>

				<ModalFooter>
					<Button colorScheme='blue' mr={3} type="submit">
						Salvar
					</Button>
					<Button
						variant='ghost'
						onClick={() => {
							onRequestClose();
							reset();
							setEndModal({} as EndData)
						}}
					>
						Cancelar
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	)
}

export default NewEndClienteModal;
